import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Center, Paper, Table, Card, SimpleGrid, Divider, Checkbox, Button, Stack, Group, Text, Title, Badge, Loader, SegmentedControl } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconClockExclamation, IconFlagFilled, IconMailCheck } from '@tabler/icons-react';
import axios from 'axios';
import { ErrorToast, SuccessToast } from '../Toasters';
import PopOverCustomButton from '../CustomComponents/Buttons/PopOverCustomButton';
import ConfirmPopover from '../ConfirmPopover';
import { epochToStringNoT } from '../../EpochToString';

const MembershipRequestApproval = () => {
    const location = useLocation();
    const isMobile = useMediaQuery('(max-width: 48em)');
    const [link, setLink] = useState(null);
    const [groups, setGroups] = useState([]);
    const [view, setView] = useState("group");
    const [dates, setDates] = useState({});
    const [selected, setSelected] = useState([]);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [resendLinkLoading, setResendLinkLoading] = useState(false);
    const [err, setErr] = useState("");
    const [approver, setApprover] = useState(null);
    const [resentSuccess, setResentSuccess] = useState(false);
    const sitesOf = (m) => Object.values(m?.cip_dates ?? {});
    const dateKey = (m, site, field) => `${m.userId}|${site.site_id}|${field}`;
    const getDate = (m, site, field) => dates[dateKey(m, site, field)] ?? site.cip_dates?.[field] ?? null;
    const isExtension = (m) => m.decisionType === "EXTENSION";
    const cipComplete = (m) => isExtension(m) || !m.cipMedium || (sitesOf(m).length > 0 && sitesOf(m).every((s) => getDate(m, s, "pra_date") && getDate(m, s, "training_date")));
    const isOpen = (m) => (isExtension(m) ? m.extension?.status === "Pending" : m.status === "Pending");
    const isPriority = (m) => m.priority === true && isOpen(m);
    const closedStatus = (m) => (isExtension(m) ? m.extension?.status : m.status);
    const justification = (m) => (isExtension(m) ? m.extension?.reason : m.reason);
    const endLabel = (m) => (isExtension(m) ? `${epochToStringNoT(m.endDate)} → ${epochToStringNoT(m.extension?.endDate)}` : epochToStringNoT(m.endDate));
    const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

    const views = {
        group: {
            label: "By Group",
            key: (m) => m.group?.id,
            title: (m) => m.group?.groupName,
            subtitle: (m) => m.group?.description,
        },
        user: {
            label: "By User",
            key: (m) => m.userId,
            title: (m) => [m.userInfo?.firstName, m.userInfo?.lastName].filter(Boolean).join(" ") || m.userInfo?.username,
            subtitle: (m) => m.userInfo?.username,
        },
        site: {
            label: "By Site",
            key: (m) => m.site?.id,
            title: (m) => m.site?.label,
            subtitle: () => null,
        },
    };

    const uniqueById = (rows) => [...new Map(rows.map((m) => [m.id, m])).values()];

    const groupBy = (rows, keyOf) => {
        const map = new Map();
        rows.forEach((m) => map.set(keyOf(m), [...(map.get(keyOf(m)) ?? []), m]));
        return [...map].map(([key, items]) => ({ key, items: uniqueById(items) }));
    };

    const fetchApprovals = () => {
        const link = new URLSearchParams(location.search).get("link");
        setLink(link);
        setApprover(null);
        setLoading(true);
        axios({ method: "GET", url: '/api/membership/approval', params: { link: link } }).then((r) => {
            setApprover(r.data?.approver ?? null);
            if (r.data?.expired) {
                setErr("expired");
            } else {
                setGroups(r.data?.results ?? []);
            }
            setSelected([]);
            setDates({});
        })
            .catch((e) => setErr(e.response?.data?.detail ?? "This request could not be loaded."))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchApprovals();
    }, [location]);

    const handleLinkRequest = () => {
        if (resendLinkLoading || resentSuccess) {
            return;
        }
        setResendLinkLoading(true);
        const link = new URLSearchParams(location.search).get("link");
        axios({ method: "GET", url: '/api/membership/approval/resend', params: { link: link } }).then(() => {
            SuccessToast({ message: "A new link has been sent to your email." });
            setResentSuccess(true);
            setResendLinkLoading(false);
        }).catch((e) => {
            setErr(e.response?.data?.detail ?? "An error occurred while requesting a new link.");
            setResendLinkLoading(false);
        })
    };

    const submit = (rows, decision, reason = "") => {
        if (decision === "approve" && rows.some((m) => !cipComplete(m))) {
            ErrorToast("Please enter PRA and Training Date for all sites");
            return;
        }
        setBusy(true);
        const post = (m) => axios({
            method: "POST",
            url: '/api/membership/approval',
            data: {
                link,
                membership_id: m.id,
                decision,
                reason,
                cip_dates: decision === "approve" && !isExtension(m) ? sitesOf(m).map((s) => ({
                    siteId: s.site_id,
                    userId: m.userId,
                    id: s.cip_dates?.id ?? null,
                    pra_date: getDate(m, s, "pra_date"),
                    training_date: getDate(m, s, "training_date"),
                })) : [],
            },
        });
        rows.reduce((chain, m) => chain.then((failed) => post(m).then(() => failed, (e) => [...failed, e])), Promise.resolve([]))
            .then((failed) => {
                failed.forEach((e) => ErrorToast(e.response?.data?.detail ?? "This request could not be updated."));
                const done = rows.length - failed.length;
                if (done > 0) {
                    SuccessToast({ message: `${done} request${done === 1 ? "" : "s"} ${decision === "approve" ? "approved" : "denied"}` });
                }
            })
            .finally(() => {
                setBusy(false);
                fetchApprovals();
            });
    };

    // priority requests go first, and so do the sections holding them
    const allMemberships = groups.flatMap((g) => g.memberships.map((m) => ({ ...m, group: g.group, site: g.site }))).sort((a, b) => isPriority(b) - isPriority(a));
    const selectedRows = uniqueById(allMemberships.filter((m) => selected.includes(m.id)));
    const sections = groupBy(allMemberships, views[view].key);
    const priorityCount = uniqueById(allMemberships.filter(isPriority)).length;

    const renderField = (label, value) => (
        <Group justify="space-between" gap="md" wrap="nowrap" align="flex-start">
            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{label}</Text>
            <Text size="sm" ta="right">{value}</Text>
        </Group>
    );

    const renderTypeBadge = (m) => (
        <>
            {isPriority(m) ? <Badge size="xs" color="red" variant="filled" leftSection={<IconFlagFilled size={10} />}>Priority</Badge> : null}
            {isExtension(m) ? <Badge size="xs" color="orange" variant="light">Extension</Badge> : null}
        </>
    );
    const priorityStyle = (m) => (isPriority(m) ? { boxShadow: 'inset 4px 0 0 var(--mantine-color-red-6)' } : undefined);

    const renderIdentity = (m) => (
        view === "user" ? (
            <Stack gap={0}>
                <Group gap="xs" wrap="nowrap">
                    <Text size="sm" fw={500}>{m.group?.groupName}</Text>
                    {renderTypeBadge(m)}
                </Group>
                {m.group?.description ? <Text size="xs" c="dimmed">{m.group.description}</Text> : null}
            </Stack>
        ) : (
            <Stack gap={0}>
                <Group gap="xs" wrap="nowrap">
                    <Text size="sm" fw={500}>{m.userInfo?.firstName} {m.userInfo?.lastName}</Text>
                    {renderTypeBadge(m)}
                </Group>
                <Text size="xs" c="dimmed" ff="monospace">{m.userInfo?.username}</Text>
                {view === "site" ? <Text size="xs" c="dimmed">{m.group?.groupName}</Text> : null}
            </Stack>
        )
    );

    const renderCip = (m) => (
        <Stack gap="xs">
            {sitesOf(m).map((site) => (
                <Stack key={site.site_id} gap={4}>
                    <Text size="xs" fw={600}>{site.label}</Text>
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                        {["pra_date", "training_date"].map((field) => (
                            <DatePickerInput
                                key={field}
                                size="xs"
                                label={field === "pra_date" ? "PRA Date" : "Training Date"}
                                valueFormat="MM/DD/YYYY"
                                disabled={!isOpen(m) || isExtension(m)}
                                value={getDate(m, site, field)}
                                onChange={(v) => setDates((d) => ({ ...d, [dateKey(m, site, field)]: v }))}
                            />
                        ))}
                    </SimpleGrid>
                </Stack>
            ))}
        </Stack>
    );

    const renderDecision = (m, grow = false) => {
        if (!isOpen(m)) {
            return <Badge color={closedStatus(m) === "Approved" ? "green" : "red"}>{closedStatus(m)}</Badge>;
        }
        if (selected.length > 0) {
            return null;
        }
        return (
            <Group gap="xs" grow={grow} wrap="nowrap">
                <ConfirmPopover
                    withinPortal
                    text={isExtension(m)
                        ? `Approve extension for ${m.userInfo?.firstName} ${m.userInfo?.lastName} until ${epochToStringNoT(m.extension?.endDate)}?`
                        : `Approve access for ${m.userInfo?.firstName} ${m.userInfo?.lastName}?`}
                    focusColor="blue"
                    trigger={<Button size={grow ? "sm" : "xs"} fullWidth={grow} color="green" loading={busy} disabled={!cipComplete(m)}>Approve</Button>}
                    callback={() => submit([m], "approve")}
                />
                <PopOverCustomButton withinPortal fullWidth={grow} focusColor="red" color="red.7" name="Deny" size={grow ? "sm" : "xs"} variant="outline" loading={busy} callback={(v) => submit([m], "deny", v.reason)} />
            </Group>
        );
    };

    const renderSection = (key, title, subtitle, memberships) => {
        const showCip = memberships.some((m) => m.cipMedium);
        const selectable = memberships.filter(isOpen);
        const sectionSelected = selectable.filter((m) => selected.includes(m.id));
        const allSelected = selectable.length > 0 && sectionSelected.length === selectable.length;

        const selectAll = (
            <Checkbox
                disabled={selectable.length === 0}
                checked={allSelected}
                indeterminate={sectionSelected.length > 0 && !allSelected}
                onChange={() => setSelected(allSelected
                    ? selected.filter((id) => !selectable.some((m) => m.id === id))
                    : [...new Set([...selected, ...selectable.map((m) => m.id)])])}
            />
        );

        const sectionActions = sectionSelected.length > 0 ? (
            <Group gap="xs" wrap="nowrap">
                <ConfirmPopover
                    withinPortal
                    text={`Approve ${sectionSelected.length} request${sectionSelected.length === 1 ? "" : "s"}${title ? ` in ${title}` : ""}?`}
                    focusColor="blue"
                    trigger={<Button size="xs" color="green" loading={busy} disabled={sectionSelected.some((m) => !cipComplete(m))}>Approve</Button>}
                    callback={() => submit(sectionSelected, "approve")}
                />
                <PopOverCustomButton withinPortal focusColor="red" color="red.7" name="Deny" size="xs" variant="outline" loading={busy} callback={(v) => submit(sectionSelected, "deny", v.reason)} />
            </Group>
        ) : null;

        return (
            <Stack key={key} gap="xs">
                <Stack gap={0}>
                    <Title order={4}>{title}</Title>
                    {subtitle ? <Text size="sm" c="dimmed">{subtitle}</Text> : null}
                </Stack>

                {isMobile ? (
                    <Stack gap="sm">
                        <Group justify="space-between" wrap="nowrap">
                            <Group gap="xs">{selectAll}<Text size="xs" c="dimmed">Select all</Text></Group>
                            {sectionActions}
                        </Group>
                        {memberships.map((m) => (
                            <Card key={m.id} withBorder radius="md" padding="md" bg={isPriority(m) ? "var(--mantine-color-red-light)" : undefined} style={isPriority(m) ? { borderColor: 'var(--mantine-color-red-6)' } : undefined}>
                                <Stack gap="sm">
                                    <Group justify="space-between" wrap="nowrap" align="flex-start">
                                        {renderIdentity(m)}
                                        <Checkbox disabled={!isOpen(m)} checked={selected.includes(m.id)} onChange={() => toggle(m.id)} />
                                    </Group>
                                    <Divider />
                                    <Stack gap={6}>
                                        {renderField("Business Justification", justification(m))}
                                        {renderField("Start", epochToStringNoT(m.startDate))}
                                        {renderField("End", endLabel(m))}
                                    </Stack>
                                    {m.cipMedium ? <><Divider />{renderCip(m)}</> : null}
                                    <Divider />
                                    {renderDecision(m, true)}
                                </Stack>
                            </Card>
                        ))}
                    </Stack>
                ) : (
                    <Table verticalSpacing="sm" highlightOnHover withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{ width: 40 }}>{selectAll}</Table.Th>
                                <Table.Th>{view === "user" ? "Group" : "User"}</Table.Th>
                                <Table.Th>Business Justification</Table.Th>
                                <Table.Th>Start Date</Table.Th>
                                <Table.Th>End Date</Table.Th>
                                {showCip ? <Table.Th style={{ minWidth: 340 }}>PRA / Training dates</Table.Th> : null}
                                <Table.Th style={{ width: 190 }}>{sectionActions ?? "Decision"}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {memberships.map((m) => (
                                <Table.Tr key={m.id} bg={isPriority(m) ? "var(--mantine-color-red-light)" : undefined} style={priorityStyle(m)}>
                                    <Table.Td>
                                        <Checkbox disabled={!isOpen(m)} checked={selected.includes(m.id)} onChange={() => toggle(m.id)} />
                                    </Table.Td>
                                    <Table.Td>{renderIdentity(m)}</Table.Td>
                                    <Table.Td><Text size="sm">{justification(m)}</Text></Table.Td>
                                    <Table.Td><Text size="sm" ff="monospace">{epochToStringNoT(m.startDate)}</Text></Table.Td>
                                    <Table.Td><Text size="sm" ff="monospace">{endLabel(m)}</Text></Table.Td>
                                    {showCip ? <Table.Td>{renderCip(m)}</Table.Td> : null}
                                    <Table.Td>{renderDecision(m)}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </Stack>
        );
    };

    return (
        <Center style={{ minHeight: '90vh', padding: isMobile ? 8 : 12 }}>
            <Paper shadow={isMobile ? "none" : "md"} withBorder={!isMobile} p={isMobile ? "xs" : "md"} w="100%" maw={1200}>
                {loading ? <Center><Loader /></Center>
                    : err ? (
                        <Center>
                            <Stack gap="md" align="center" maw={420} py="xl" style={{ textAlign: 'center' }}>
                                {err === "expired"
                                    ? resentSuccess
                                        ? <>
                                            <IconMailCheck size={44} stroke={1.5} color="var(--mantine-color-green-6)" />
                                            <Title order={3}>Success</Title>
                                            <Text c="dimmed">Link sent successfully.</Text>
                                        </>
                                        : <>
                                            <IconClockExclamation size={44} stroke={1.5} color="var(--mantine-color-dimmed)" />
                                            <Title order={3}>Link expired</Title>
                                            <Text c="dimmed">For security, this link has expired. Please request a new link below.</Text>
                                            <Button fullWidth size="md" variant="light" loading={resendLinkLoading} disabled={resentSuccess} onClick={() => handleLinkRequest()}>
                                                {resentSuccess ? "Link sent" : "Request a new link"}
                                            </Button>
                                        </>
                                    : <Text c="red">{err}</Text>}
                                {approver ? <Text size="xs" c="dimmed">Approver: {approver}</Text> : null}
                            </Stack>
                        </Center>
                    )
                        : (
                            <Stack gap="xl">
                                <Group
                                    justify="space-between"
                                    align="center"
                                    wrap="wrap"
                                    style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--mantine-color-body)', paddingTop: 8, paddingBottom: 8 }}
                                >
                                    <Group gap="md">
                                        <Stack gap={2}>
                                            <Group gap="xs">
                                                <Title order={3}>Membership requests</Title>
                                                {priorityCount > 0 ? <Badge color="red" variant="filled" leftSection={<IconFlagFilled size={12} />}>{priorityCount} priority</Badge> : null}
                                            </Group>
                                            {approver ? <Text size="sm" c="dimmed">Approver: {approver}</Text> : null}
                                        </Stack>
                                        <SegmentedControl
                                            size="xs"
                                            value={view}
                                            onChange={setView}
                                            data={Object.entries(views).map(([value, v]) => ({ label: v.label, value }))}
                                        />
                                    </Group>
                                    {selected.length > 0 ? (
                                        <Group gap="xs">
                                            <Text size="sm" c="dimmed">{selected.length} selected</Text>
                                            <ConfirmPopover
                                                withinPortal
                                                text={`Approve ${selected.length} request${selected.length === 1 ? "" : "s"}?`}
                                                focusColor="blue"
                                                trigger={<Button size="xs" color="green" loading={busy} disabled={selectedRows.some((m) => !cipComplete(m))}>Approve</Button>}
                                                callback={() => submit(selectedRows, "approve")}
                                            />
                                            <PopOverCustomButton withinPortal focusColor="red" color="red.7" name="Deny" size="xs" variant="outline" loading={busy} callback={(v) => submit(selectedRows, "deny", v.reason)} />
                                        </Group>
                                    ) : null}
                                </Group>

                                {sections.map((s) => renderSection(s.key, views[view].title(s.items[0]), views[view].subtitle(s.items[0]), s.items))}
                            </Stack>
                        )}
            </Paper>
        </Center>
    );
};

export default MembershipRequestApproval;